import { EpubCFI } from 'epubjs';
import type { EpubCFIComponent } from 'epubjs/types/epubcfi';
import useReaderEngine from '../engine/use-reader-engine';
import useMediaStore from '../stores/media';

export type BookLocation = {
  epubCFI?: string;
  temporalOffsetSeconds?: number;
};

/**
 * Fixes malformed CFIs that are missing the spine ID assertion.
 *
 * For continuous books with custom view managers, epub.js sometimes generates
 * CFIs without the spine page identifier, which causes the annotations API to fail.
 *
 * @param cfi - The CFI to fix (may be malformed)
 * @param spineId - The spine ID to insert (e.g., "page-2")
 * @returns The fixed CFI with spine ID assertion included
 *
 * @example
 * // Input: epubcfi(/6/2!/4/4/4/2[text-1])
 * // Output: epubcfi(/6/2[page-2]!/4/4/4/2[text-1])
 */
export const fixCfiSpineId = (cfi: string, spineId?: string): string => {
  if (!cfi || !spineId) return cfi;

  // Check if spine ID assertion is missing (e.g., /6/2! instead of /6/2[page-id]!)
  const missingSpineIdPattern = /epubcfi\(\/6\/(\d+)!\//;
  const hasSpineIdPattern = /epubcfi\(\/6\/\d+\[[^\]]+\]!\//;

  if (missingSpineIdPattern.test(cfi) && !hasSpineIdPattern.test(cfi)) {
    // Insert the spine ID between the spine index and the "!"
    return cfi.replace(/epubcfi\(\/6\/(\d+)!\//, `epubcfi(/6/$1[${spineId}]!/`);
  }

  return cfi;
};

/**
 * Extracts the spine ID from a CFI string.
 *
 * @param cfi - The CFI to extract from
 * @returns The spine ID (e.g., "page-2") or undefined if not found
 *
 * @example
 * // Input: epubcfi(/6/2[page-2]!/4/4/4/2[text-1])
 * // Output: "page-2"
 */
export const extractSpineIdFromCfi = (cfi: string): string | undefined => {
  const match = cfi.match(/\/6\/\d+\[([^\]]+)\]!/);
  return match ? match[1] : undefined;
};

/**
 * Replaces the spine path in a target CFI with the spine path from a source CFI.
 *
 * This is useful when converting element-only CFIs to range CFIs in continuous books,
 * where section.cfiFromRange() may generate a CFI with the wrong spine index.
 *
 * @param targetCfi - The CFI whose spine path should be replaced
 * @param sourceCfi - The CFI to copy the spine path from
 * @returns The target CFI with the source's spine path, or the original target CFI if replacement fails
 *
 * @example
 * // Target:  epubcfi(/6/2!/4/4/4/2/2[text-1]/2,/1:0,/1:17)
 * // Source:  epubcfi(/6/4[page-3]!/4/2/4/2[text-1])
 * // Result:  epubcfi(/6/4[page-3]!/4/4/4/2/2[text-1]/2,/1:0,/1:17)
 */
export const replaceCfiSpinePath = (targetCfi: string, sourceCfi: string): string => {
  if (!targetCfi || !sourceCfi) return targetCfi;

  // Extract the spine path from the source CFI (everything before and including the "!")
  const sourceSpineMatch = sourceCfi.match(/epubcfi\((\/6\/\d+(?:\[[^\]]+\])?!)\//);
  const targetSpineMatch = targetCfi.match(/epubcfi\((\/6\/\d+(?:\[[^\]]+\])?!?)\//);

  if (sourceSpineMatch && targetSpineMatch) {
    const sourceSpinePath = sourceSpineMatch[1]; // e.g., "/6/4[page-3]!"
    const targetSpinePath = targetSpineMatch[1]; // e.g., "/6/2!" or "/6/2[wrong-id]!"

    // Return early ONLY if they are already exactly the same
    if (sourceSpinePath === targetSpinePath) {
      return targetCfi;
    }

    // Replace the target spine path with the source one
    return targetCfi.replace(`epubcfi(${targetSpinePath}/`, `epubcfi(${sourceSpinePath}/`);
  }

  return targetCfi;
};

export const parseLocation = (epubCFI?: string): BookLocation => {
  if (!epubCFI) {
    return { epubCFI: undefined };
  }
  const temporalIndex = epubCFI.indexOf('~');
  if (temporalIndex === -1) {
    return {
      epubCFI,
    };
  }

  const temporalOffset = parseFloat(epubCFI.slice(temporalIndex + 1, -1));
  const cfi = `${epubCFI.substring(0, temporalIndex)})`;

  return {
    epubCFI: cfi,
    temporalOffsetSeconds: temporalOffset,
  };
};

export const createEpubCFI = (location: BookLocation): string => {
  const { epubCFI, temporalOffsetSeconds } = location;

  if (epubCFI && location.temporalOffsetSeconds) {
    return epubCFI.slice(0, -1).concat(`~${temporalOffsetSeconds})`);
  }

  return epubCFI || '';
};

export const getLocation = async (): Promise<BookLocation> => {
  const { getCurrentEpubCFI, getElementCFI } = useReaderEngine();
  const media = useMediaStore();

  // if we have a track playing, it'll give us the most accurate location
  const track = media.currentTrack;
  if (track) {
    const cfi = await getElementCFI(track.text.srcFile, track.text.srcFragmentId);
    return {
      epubCFI: cfi,
      temporalOffsetSeconds: media.currentPlayerTime,
    };
  }

  const epubCFI = getCurrentEpubCFI();
  return {
    epubCFI,
  };
};

export const getElementFromEpubCFI = (cfi: string): string | undefined => {
  const epubCFI = new EpubCFI(cfi);
  const cfiSteps = ((epubCFI as any).path as EpubCFIComponent).steps;
  // Returns the target element ID (the innermost/last ID) specified by the CFI.
  return [...cfiSteps].reverse().find((step) => step.id)?.id;
};

// Like getElementFromEpubCFI but also handles range CFIs by collapsing to the start point first.
export const parseCfiRange = (cfi: string): { startCfi: string; endCfi: string } | null => {
  if (!cfi.startsWith('epubcfi(') || !cfi.endsWith(')')) return null;
  const inner = cfi.slice(8, -1);
  const [i1, i2] = [inner.indexOf(','), inner.lastIndexOf(',')];
  if (i1 === -1 || i2 === -1 || i1 === i2) return null;
  const base = inner.slice(0, i1);
  return {
    startCfi: `epubcfi(${base}${inner.slice(i1 + 1, i2)})`,
    endCfi: `epubcfi(${base}${inner.slice(i2 + 1)})`,
  };
};

// epub.js sometimes produces range CFIs where the start path is deeper than the end path
// (e.g. start=.../f000415]/2/1:0, end=.../f000415]/1:28) when the selection spans from inside
// a child element into a sibling text node. CFI compare then sees end < start and overlap
// detection fails. Normalise each range so the earlier position is always treated as start.
const normaliseCfiRange = (
  epubCfi: EpubCFI,
  r: { startCfi: string; endCfi: string },
): { startCfi: string; endCfi: string } => {
  try {
    return epubCfi.compare(r.startCfi, r.endCfi) <= 0 ? r : { startCfi: r.endCfi, endCfi: r.startCfi };
  } catch {
    return r;
  }
};

// A overlaps B if A.end > B.start AND B.end > A.start
export const cfiRangesOverlap = (cfi1: string, cfi2: string): boolean => {
  const r1 = parseCfiRange(cfi1);
  const r2 = parseCfiRange(cfi2);
  if (!r1 || !r2) return false;
  try {
    const epubCfi = new EpubCFI();
    const n1 = normaliseCfiRange(epubCfi, r1);
    const n2 = normaliseCfiRange(epubCfi, r2);
    return epubCfi.compare(n1.endCfi, n2.startCfi) > 0 && epubCfi.compare(n2.endCfi, n1.startCfi) > 0;
  } catch {
    return false;
  }
};

// Returns true if innerCfi is fully contained within outerCfi
export const isCfiRangeContainedIn = (innerCfi: string, outerCfi: string): boolean => {
  const inner = parseCfiRange(innerCfi);
  const outer = parseCfiRange(outerCfi);
  if (!inner || !outer) return false;
  try {
    const epubCfi = new EpubCFI();
    const ni = normaliseCfiRange(epubCfi, inner);
    const no = normaliseCfiRange(epubCfi, outer);
    return epubCfi.compare(no.startCfi, ni.startCfi) <= 0 && epubCfi.compare(ni.endCfi, no.endCfi) <= 0;
  } catch {
    return false;
  }
};

export const getElementFromCfi = (cfi: string): string | undefined => {
  try {
    const epubCfi = new EpubCFI(cfi);
    if (epubCfi.range) epubCfi.collapse(true);
    return getElementFromEpubCFI(epubCfi.toString());
  } catch {
    return undefined;
  }
};
